import type { PaymentIntent } from "@stripe/stripe-js";
import { addOnDbValue } from "src/constants/options";
import { withZip } from "../lib/address";
import { arrivalFor } from "../lib/proximity";
import { petsLabel } from "../lib/pets";
import { initialState } from "../reducers";
import {
  BookingInput,
  HttpError,
  TaskInput,
  VerifyPaymentInput,
} from "../types";

export const createPaymentIntent = async (
  payload: BookingInput | TaskInput,
  token?: string,
  source: "bookings" | "tasks" = "bookings"
): Promise<PaymentIntent | HttpError> => {
  if (source === "bookings") {
    // replace booking.add_ons with addOnDbValue to get past the 500 character limit
    // set by stripe for their metadata string.
    if ("add_ons" in payload) {
      const addOns = payload.add_ons
        .filter(Boolean)
        .map((addOn) => addOnDbValue[addOn] || "");
      payload.add_ons = addOns;
    }
  }

  let response = await fetch(
    process.env.REACT_APP_API_URL + `/${source || "bookings"}/initialize`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(payload),
    }
  );

  return readJson(response);
};

/**
 * A payment-intent response's body, with the status riding along on a refusal
 * so checkout can tell "the price moved" (409) from every other reason a
 * booking was turned down.
 */
async function readJson(response: Response) {
  const body = await response.json();
  return response.ok ? body : { ...body, status: response.status };
}

export const updatePaymentIntent = async (
  paymentIntent: PaymentIntent,
  payload: BookingInput | TaskInput,
  token?: string,
  source: "bookings" | "tasks" = "bookings"
): Promise<PaymentIntent | HttpError> => {
  if ("add_ons" in payload) {
    // replace booking.add_ons with addOnDbValue to get past the 500 character limit
    // set by stripe for their metadata string.
    const addOns = payload.add_ons
      .filter(Boolean)
      .map((addOn) => addOnDbValue[addOn] || "");
    payload.add_ons = addOns;
  }

  let response = await fetch(
    process.env.REACT_APP_API_URL +
      `/${source || "bookings"}/payment-intent/${paymentIntent.id}`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(payload),
    }
  );

  return readJson(response);
};

export const verifyPayment = async ({
  booking,
  payment_type = "cash",
  payment_intent = null,
  payment_method,
  token,
  tasks,
}: VerifyPaymentInput): Promise<
  | {
      data: {
        fullname: string;
        email: string;
        clean_date: string;
        scheduled_date: string;
        total_amount: number;
        bookingId: string;
        /**
         * Authorizes `POST /instructions` for this booking — see
         * `IConfirmation.instructionsToken`. Only `/bookings/verify` mints one;
         * `/tasks/verify` does not, so it can be absent.
         */
        instructionsToken?: string;
      };
    }
  | HttpError
> => {
  if (booking) {
    const addOns = booking.add_ons
      .filter(Boolean)
      .map((addOn) => addOnDbValue[addOn] || "");
    booking.add_ons = addOns;
  }
  const source = tasks ? "tasks" : "bookings";
  let response = await fetch(
    process.env.REACT_APP_API_URL + `/${source}/verify`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        ...booking,
        payment_intent,
        payment_type,
        payment_method,
        tasks,
      }),
    }
  );

  return await response.json();
};

type ContextType = {
  state: typeof initialState;
  email?: string;
  phone?: string;
  contactType?: string;
  fullname?: string;
};

let getFrequency = (occurance: number): string => {
  switch (occurance) {
    case 0:
      return "One Time";
    case 20:
      return "Weekly";
    case 15:
      return "Every 2 weeks";
    case 10:
      return "Every 4 weeks";
    default:
      return "One Time";
  }
};

export const getBookingDetails = ({
  state,
  email,
  phone,
  contactType: contact_type,
  fullname,
}: ContextType): BookingInput => {
  return {
    email,
    fullname,
    bathrooms: parseInt(state.bathrooms, 10),
    bedrooms: state.bedrooms,
    clean_date: state.cleanDate,
    cleaning_type: state.types,
    /**
     * A Same-Day booking's arrival window stands in for the clean time. The window
     * label is what the customer agreed to, and every existing consumer of
     * `clean_time` — the confirmation email, Slack, the admin panel — reads a
     * string, so sending the label keeps them all correct without a change each.
     *
     * The *label*, emphatically: this sent `arrivalWindow` — the window id — until
     * the store started keeping both, so the confirmation email told the customer
     * their cleaner would arrive at "midday". `arrival_window` below is where the
     * id belongs, because the server matches it against its own configured
     * windows. The fallback covers a booking restored from a store written before
     * the label existed.
     */
    clean_time: arrivalFor(state),
    /**
     * Whether Same-Day was taken, and which window. The *fact* of the choice, never
     * the surcharge: the server re-prices from its own config and would reject a
     * client-supplied amount. See
     * `docs/adr/0006-pricing-config-is-public-integrity-is-not.md`.
     */
    same_day: Boolean(state.sameDay),
    arrival_window: state.sameDay ? state.arrivalWindow : undefined,
    frequency: getFrequency(state.occurance),
    promocode: state.code,
    add_ons: state.addOns.type.length ? state.addOns.type : [""],
    address: withZip(state.address, state.zip),
    /**
     * The ZIP the address checker pinned, sent so the booking can be found by
     * location. Re-checked against `service_area` server-side and stored as null
     * if it does not hold up — the same rule as `same_day` above: the client
     * sends the fact, the server decides what it means.
     */
    zip: state.zip || null,
    /**
     * The signed area-pricing tier from the quote. The server charges the tier
     * inside it only if it still matches this ZIP, date and arrival; otherwise it
     * works the tier out again and, if that changes the total, refuses with 409
     * before anything is charged.
     */
    proximity_token: state.proximityToken || null,
    appartment_no: state.appartmentNo,
    // tip: state.tip,
    tip: 0,
    // avoid sending confirmation email if type is "test".
    instructions:
      process.env.REACT_APP_TYPE === "test" ? "test" : state.instructions,
    pets: petsLabel(state.pets),
    phone,
    get_in_type: state.getInType.text || state.getInType.type,
    contact_type,
    total_amount: state.totalPrice,
  };
};

export const createInstructionsAPI = async (
  instructions: any,
  token?: string
): Promise<any | HttpError> => {
  const formData = new FormData();
  formData.append("bookingId", instructions.bookingId);
  // Authorizes this POST for the booking; issued by /bookings/verify.
  formData.append("instructionsToken", instructions.instructionsToken || "");
  formData.append("cleaningTools", instructions.cleaningTools);
  formData.append("cleaningInstructions", instructions.cleaningInstructions);
  formData.append(
    "specialAreasInstructions",
    instructions.specialAreasInstructions
  );

  formData.append("delicateObjects", instructions.delicateObjects);
  formData.append("delicateOthers", instructions.delicateOthers);
  formData.append("delicateInstructions", instructions.delicateInstructions);

  // Fix: Append each file individually instead of the entire array
  if (
    instructions.specialAreasFiles &&
    Array.isArray(instructions.specialAreasFiles)
  ) {
    instructions.specialAreasFiles.forEach((file: File) => {
      formData.append("specialAreasFiles", file);
    });
  }

  if (instructions.delicateFiles && Array.isArray(instructions.delicateFiles)) {
    instructions.delicateFiles.forEach((file: File) => {
      formData.append("delicateFiles", file);
    });
  }

  let response = await fetch(process.env.REACT_APP_API_URL + "/instructions", {
    method: "POST",
    body: formData,
  });
  return await response.json();
};

export interface CustomTaskRequest {
  task_description: string;
  area: string;
  email: string;
  special_instructions?: string;
}

export const submitCustomTaskRequest = async (
  request: CustomTaskRequest
): Promise<{ success: boolean; message?: string; error?: string }> => {
  try {
    const response = await fetch(
      process.env.REACT_APP_API_URL + "/tasks/custom-request",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(request),
      }
    );

    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: data.error || data.message || "Failed to submit request",
      };
    }

    return {
      success: true,
      message: data.message || "Request submitted successfully",
    };
  } catch (error) {
    console.error("Error submitting custom task request:", error);
    return {
      success: false,
      error: "Network error. Please check your connection and try again.",
    };
  }
};
